/**
 * Support Ticket Actions for Customer Confidence Call
 * Handles API calls and utilities related to customer support ticket data.
 * Mirrors the outage actions pattern (see incidentActions.ts).
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { getDataFromSessionStorage, validateBackendUrl } from "../../../config/configHelper";
import { HttpService } from "../../../library/http/http.service";
import { logError } from "../../../library/logging/loggingService";
const ALLOWED_ORDER_BY_FIELDS = new Set([
    "caseNumber",
    "title",
    "severity",
    "state",
    "impactedProduct",
    "createdDateTime",
]);
const normalizeOrderBy = (orderBy) => {
    const fallback = "createdDateTime desc";
    if (!(orderBy === null || orderBy === void 0 ? void 0 : orderBy.trim())) {
        return fallback;
    }
    const [field, direction] = orderBy.trim().split(/\s+/, 2);
    const normalizedDirection = (direction || "desc").toLowerCase();
    if (!ALLOWED_ORDER_BY_FIELDS.has(field) ||
        (normalizedDirection !== "asc" && normalizedDirection !== "desc")) {
        return fallback;
    }
    return `${field} ${normalizedDirection}`;
};
/**
 * Parse the comma-separated linkedIcmIds string into a trimmed list.
 */
const parseLinkedIcmIds = (linkedIcmIds) => {
    if (!(linkedIcmIds === null || linkedIcmIds === void 0 ? void 0 : linkedIcmIds.trim())) {
        return [];
    }
    return linkedIcmIds
        .split(",")
        .map((id) => id.trim())
        .filter(Boolean);
};
/**
 * Map a backend SupportTicketItem to the UI SupportTicketData model.
 */
const mapSupportTicketItemToData = (item) => {
    var _a, _b, _c, _d, _e, _f, _g;
    return ({
        caseNumber: String((_a = item.caseNumber) !== null && _a !== void 0 ? _a : ""),
        title: (_b = item.title) !== null && _b !== void 0 ? _b : "",
        description: (_c = item.description) !== null && _c !== void 0 ? _c : "",
        linkedIcmIds: parseLinkedIcmIds(item.linkedIcmIds),
        impactedProduct: (_d = item.impactedProduct) !== null && _d !== void 0 ? _d : "",
        severity: (_e = item.severity) !== null && _e !== void 0 ? _e : "",
        state: (_f = item.state) !== null && _f !== void 0 ? _f : "",
        createdDateTime: (_g = item.createdDateTime) !== null && _g !== void 0 ? _g : "",
        isS400: Boolean(item.isS400),
    });
};
/**
 * Fetch support tickets from the backend API for a given TPID.
 * Returns both the page of SupportTicketData items and the total server-side count.
 */
export const fetchSupportTicketsByTpid = (params) => __awaiter(void 0, void 0, void 0, function* () {
    var _a, _b, _c, _d;
    const { tpid, days = 28, search, page = 1, pageSize = 50, orderBy = "createdDateTime desc", } = params;
    const normalizedTpid = tpid === null || tpid === void 0 ? void 0 : tpid.trim();
    if (!normalizedTpid || !/^\d+$/.test(normalizedTpid)) {
        throw new Error("Invalid TPID. Expected numeric account identifier.");
    }
    const safePageSize = Math.min(200, Math.max(1, pageSize));
    const safePage = Math.max(1, page);
    const http = new HttpService();
    // Validate the session-storage-sourced base URL against the HTTPS + hostname
    // allowlist before issuing the request (session storage is tamperable, so an
    // unvalidated value is an SSRF / data-exfiltration risk). Mirrors the
    // write-path guard in submissionActions.ts.
    const urlResult = validateBackendUrl(getDataFromSessionStorage("globalFrontdoorbaseurl"));
    if (!urlResult.valid) {
        throw new Error(`Invalid backend URL configuration: ${urlResult.reason}`);
    }
    const route = `/api/accounts/${encodeURIComponent(normalizedTpid)}/support-tickets`;
    const queryParams = new URLSearchParams();
    const safeDays = Math.min(90, Math.max(1, days));
    const safeOrderBy = normalizeOrderBy(orderBy);
    queryParams.set("days", String(safeDays));
    queryParams.set("page", String(safePage));
    queryParams.set("pageSize", String(safePageSize));
    queryParams.set("orderBy", safeOrderBy);
    if (search === null || search === void 0 ? void 0 : search.trim()) {
        queryParams.set("search", search.trim());
    }
    const response = (yield http.get(urlResult.origin, route, queryParams));
    const rawData = response === null || response === void 0 ? void 0 : response.data;
    const body = rawData && ("items" in rawData || "value" in rawData)
        ? rawData
        : rawData === null || rawData === void 0 ? void 0 : rawData.result;
    const ticketItems = (_b = (_a = body === null || body === void 0 ? void 0 : body.items) !== null && _a !== void 0 ? _a : body === null || body === void 0 ? void 0 : body.value) !== null && _b !== void 0 ? _b : [];
    const items = ticketItems.map(mapSupportTicketItemToData);
    const totalCount = (_d = (_c = body === null || body === void 0 ? void 0 : body.totalCount) !== null && _c !== void 0 ? _c : body === null || body === void 0 ? void 0 : body.odataCount) !== null && _d !== void 0 ? _d : items.length;
    return {
        items,
        totalCount,
    };
});
/**
 * Format the support ticket created date/time for display.
 * @param dateTimeString - ISO date string
 * @returns string - Formatted date/time
 */
export const formatSupportTicketCreatedTime = (dateTimeString) => {
    if (!dateTimeString) {
        return "";
    }
    const date = new Date(dateTimeString);
    if (isNaN(date.getTime())) {
        logError("Invalid support ticket created date", {
            component: "supportTicketActions",
            action: "formatSupportTicketCreatedTime",
        });
        return dateTimeString;
    }
    return date.toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        timeZoneName: "short",
    });
};
